import { Component, inject, Input, ElementRef, ViewChild, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { MediaPlayerHelperService } from '../../media-player/services/media-player-helper.service';
import { FileInfo } from '../../../interfaces';
import { MediaType } from '../../../models/media-player-types';

export interface ViewerInfoFile {
  displayName?: string;
  size?: number;
  contentType?: string;
  timeCreated?: number;
  updated?: number;
  addedTime?: number;
  metadata?: string;
  [key: string]: unknown;
}

export interface ProductionInfo {
  camera?: string;
  shotBy?: string;
  lens?: string;
  iso?: string;
  shutter?: string;
}

@Component({
  selector: 'viewer-info-button',
  templateUrl: './viewer-info-button.component.html',
  styleUrl: './viewer-info-button.component.scss',
  standalone: false,
})
export class ViewerInfoButtonComponent implements AfterViewChecked {
  @Input() file: FileInfo | null = null;
  /** When true (e.g. in video-annotation), popup uses position:fixed so it is not clipped by Video.js overflow */
  @Input() useFixedPosition: boolean = false;

  @ViewChild('popupEl') popupEl?: ElementRef<HTMLDivElement>;

  private readonly mediaPlayerHelperService: MediaPlayerHelperService = inject(MediaPlayerHelperService);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  isOpen: boolean = false;
  popupFixedStyle: { position: string; left: string; bottom: string; top: string; zIndex: number } | null = null;
  private popupPositionPending: boolean = false;
  /** When closing with useFixedPosition, keep fixed style until exit transition (300ms) finishes */
  private closeTransitionTimeout: ReturnType<typeof setTimeout> | null = null;
  private static readonly POPUP_CLOSE_TRANSITION_MS: number = 350;

  get mediaType(): string {
    return this.mediaPlayerHelperService.getMediaType(this.file);
  }

  get viewerFile(): ViewerInfoFile {
    return {
      displayName: this.file?.displayName,
      size: this.file?.size,
      contentType: this.file?.contentType,
      timeCreated: this.file?.timeCreated,
      updated: this.file?.updated,
    };
  }

  get popupTitle(): string {
    switch (this.mediaType) {
      case MediaType.VIDEO:
        return 'video_information';
      case MediaType.IMAGE:
        return 'image_information';
      case MediaType.AUDIO:
        return 'audio_information';
      default:
        return 'asset_information';
    }
  }

  get productionInfo(): ProductionInfo | null {
    if (!this.file?.metadata) return null;
    try {
      const parsed: any = JSON.parse(this.file.metadata) as Record<string, unknown>;
      const info: ProductionInfo = {};
      if (typeof parsed['camera'] === 'string') info.camera = parsed['camera'];
      if (typeof parsed['shotBy'] === 'string') info.shotBy = parsed['shotBy'];
      if (typeof parsed['lens'] === 'string') info.lens = parsed['lens'];
      if (typeof parsed['iso'] === 'string') info.iso = parsed['iso'];
      if (typeof parsed['shutter'] === 'string') info.shutter = parsed['shutter'];
      return Object.keys(info).length ? info : null;
    } catch {
      return null;
    }
  }

  public togglePopup(): void {
    this.isOpen = !this.isOpen;
    if (this.useFixedPosition) {
      if (this.isOpen) {
        this.clearCloseTransitionTimeout();
        this.popupPositionPending = true;
        this.popupFixedStyle = null;
      } else {
        this.scheduleClearPopupFixedStyle();
      }
    }
  }

  public closePopup(): void {
    this.isOpen = false;
    if (this.useFixedPosition) {
      this.scheduleClearPopupFixedStyle();
    } else {
      this.popupFixedStyle = null;
      this.popupPositionPending = false;
    }
  }

  private clearCloseTransitionTimeout(): void {
    if (this.closeTransitionTimeout != null) {
      clearTimeout(this.closeTransitionTimeout);
      this.closeTransitionTimeout = null;
    }
  }

  /** Keep fixed position during exit transition so popup slides out like in image/audio */
  private scheduleClearPopupFixedStyle(): void {
    this.clearCloseTransitionTimeout();
    this.closeTransitionTimeout = setTimeout(() => {
      this.closeTransitionTimeout = null;
      this.popupFixedStyle = null;
      this.popupPositionPending = false;
      this.cdr.markForCheck();
    }, ViewerInfoButtonComponent.POPUP_CLOSE_TRANSITION_MS);
  }

  ngAfterViewChecked(): void {
    if (!this.popupPositionPending || !this.isOpen || !this.useFixedPosition) return;
    const container: HTMLElement | null = this.elementRef.nativeElement.closest(
      '.video-annotation-container'
    ) as HTMLElement | null;
    const popup: HTMLDivElement | null = this.popupEl?.nativeElement;
    if (!container || !popup) return;
    this.popupPositionPending = false;
    requestAnimationFrame(() => {
      this.popupFixedStyle = {
        position: 'fixed',
        left: `0px`,
        top: `0px`,
        bottom: '75px',
        zIndex: 100002,
      };
      this.cdr.markForCheck();
    });
  }
}
